import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './arusha-park.css';
import {
  IMAGE_FALLBACK,
  ARUSHA_PARK_HERO_SLIDES,
  ARUSHA_PARK_PAGE_IMAGES,
} from '../../data/destinationImages';

import lionFace from '../../assets/images/lion-face.jpg';
import elephantFace from '../../assets/images/elephant-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';
import wildebeestFace from '../../assets/images/wildebeest-face.jpg';
import rhinoFace from '../../assets/images/rhino-face.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';

const ARUSHA_IMAGES = ARUSHA_PARK_PAGE_IMAGES;

const HERO_STATS = [
  { value: '552', label: 'km² park area', delay: 0 },
  { value: '4,566 m', label: 'Mount Meru summit', delay: 80 },
  { value: '400+', label: 'Bird species', delay: 160 },
  { value: '1960', label: 'Established', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: wildebeestFace,
    title: 'Giraffes',
    text: 'Maasai giraffes browse across open glades and forest margins Arusha is one of Tanzania\'s best parks for close giraffe encounters.',
    facePosition: 'center 30%',
  },
  {
    animalFace: wilddogFace,
    title: 'Colobus Monkeys',
    text: 'Black-and-white colobus troops leap through montane forest canopy a signature primate of Arusha\'s Ngurdoto and Meru woodlands.',
    facePosition: 'center 36%',
  },
  {
    animalFace: elephantFace,
    title: 'Buffalo & Zebras',
    text: 'Large buffalo herds and zebra columns graze the grasslands below Mount Meru, often alongside warthogs and bushbuck.',
    facePosition: 'center 42%',
  },
  {
    animalFace: eagleFace,
    title: 'Flamingos & Waterbirds',
    text: 'The alkaline Momella Lakes attract flamingos, pelicans, cormorants, and kingfishers excellent birding from shore or canoe.',
    facePosition: 'center 28%',
  },
  {
    animalFace: lionFace,
    title: 'Meru Slopes Wildlife',
    text: 'Higher on Mount Meru\'s forested slopes, elephants, leopards, and duikers inhabit a quieter, more rugged wilderness zone.',
    facePosition: 'center 38%',
  },
  {
    animalFace: rhinoFace,
    title: 'Forest & Grassland Birds',
    text: 'Silvery-cheeked hornbills, turacos, and narina trogons fill the forest over 400 species recorded across the park\'s varied habitats.',
    facePosition: 'center 40%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Clear skies frame Mount Meru, wildlife gathers at Momella Lakes, and forest trails stay accessible ideal for day safaris and Meru treks.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'Lush montane forest, newborn calves on the plains, and peak bird activity flamingo numbers swell on the Momella Lakes.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains bring misty Meru views and empty tracks. Canoeing and game drives still run pack a light rain jacket.',
  },
];

const EXPERIENCES = [
  {
    image: ARUSHA_IMAGES.lake,
    title: 'Momella Lakes Safari',
    text: 'Circle the park\'s chain of alkaline lakes for flamingos, hippos, and Mount Meru reflected in still water the classic Arusha game drive.',
  },
  {
    image: ARUSHA_IMAGES.forest,
    title: 'Montane Forest Walks',
    text: 'Guided walks through fig and podocarpus forest reveal colobus monkeys, waterfalls, and Tululusia Hill viewpoints above the canopy.',
  },
  {
    image: ARUSHA_IMAGES.crater,
    title: 'Ngurdoto Crater Rim',
    text: 'Stop at the "Little Ngorongoro" lookout for buffalo and baboons in the swampy crater floor a dramatic half-day highlight.',
  },
];

const GALLERY_IMAGES = [
  { src: ARUSHA_IMAGES.hero, caption: 'Mount Meru' },
  { src: ARUSHA_IMAGES.wildlife, caption: 'Park Giraffes' },
  { src: ARUSHA_IMAGES.lake, caption: 'Momella Lakes' },
  { src: ARUSHA_IMAGES.forest, caption: 'Montane Forest' },
  { src: ARUSHA_IMAGES.crater, caption: 'Ngurdoto Crater' },
  { src: ARUSHA_IMAGES.overview, caption: 'Meru Foothills' },
];

const SAFARI_PACKAGES = [
  {
    image: ARUSHA_IMAGES.lake,
    title: 'Arusha Day Safari',
    duration: '1 day',
    tag: 'Day trip',
    text: 'A full day from Arusha town Momella Lakes, forest game drives, and Ngurdoto Crater viewpoints in Tanzania\'s gateway park.',
    highlights: ['Arusha pickup', 'Lakes & forest loop', 'Crater rim stop'],
  },
  {
    image: ARUSHA_IMAGES.forest,
    title: 'Walk & Canoe Escape',
    duration: '1–2 days',
    tag: 'Active safari',
    text: 'Combine a guided montane forest walk with canoeing on Momella Lakes a rare hands-on safari close to town.',
    highlights: ['Guided walk', 'Canoe safari', 'Colobus & birds'],
  },
  {
    image: ARUSHA_IMAGES.hero,
    title: 'Meru Trek Opener',
    duration: '3–5 days',
    tag: 'Safari + trek',
    text: 'Start with a park game drive, then climb Mount Meru for crater-rim views the smart acclimatisation trip before Kilimanjaro.',
    highlights: ['Park intro day', 'Mount Meru trek', 'Arusha staging'],
  },
  {
    image: ARUSHA_IMAGES.wildlife,
    title: 'Northern Circuit Starter',
    duration: '5–7 days',
    tag: 'Most popular',
    text: 'Open with Arusha National Park, then continue to Tarangire, Manyara, Ngorongoro, and the Serengeti.',
    highlights: ['Multi-park itinerary', 'Private 4×4', 'Flexible lodges'],
  },
];

const GETTING_THERE_ROUTES = [
  'Drive from Arusha town (approx. 45 min–1 hr) one of Tanzania\'s most accessible national parks',
  'Transfer from Kilimanjaro International Airport (JRO) via Arusha ideal first-day safari or Meru trek staging point',
  'Combine with Lake Manyara, Ngorongoro, and Tarangire on a Northern Circuit itinerary',
  'Allow a full day for game drives, canoeing, and crater viewpoints or two days if trekking Mount Meru',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="arusha-park-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="arusha-park-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="arusha-park-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="arusha-park-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function ArushaPark() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="arusha-park">
      <section className="arusha-park-hero" aria-labelledby="arusha-park-hero-heading">
        <div className="arusha-park-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="arusha-park-hero" slides={ARUSHA_PARK_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="arusha-park-hero__overlay" />
        </div>
        <div className="page-container arusha-park-hero__container">
          <div className="arusha-park-hero__content">
            <nav className="arusha-park-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="arusha-park-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#safaris">Northern Circuit</a>
              <span className="arusha-park-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Arusha National Park</span>
            </nav>
            <p className="arusha-park-hero__eyebrow">Northern Safari Circuit</p>
            <h1 id="arusha-park-hero-heading">
              Arusha
              <span>National Park</span>
            </h1>
            <p className="arusha-park-hero__lead">
              Mount Meru, Momella Lakes, and Ngurdoto Crater a compact park
              at Tanzania&apos;s safari gateway where giraffes, colobus monkeys,
              and volcanic landscapes meet.
            </p>
            <div className="arusha-park-hero__actions">
              <a className="arusha-park-btn arusha-park-btn--primary" href="#contact">
                Plan your Arusha safari
              </a>
              <a className="arusha-park-btn arusha-park-btn--outline" href="#arusha-park-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="arusha-park-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="arusha-park-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="arusha-park-hero__stat-value">{stat.value}</span>
              <span className="arusha-park-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="arusha-park-overview"
        className="arusha-park-section arusha-park-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="arusha-park-overview__grid">
            <Reveal className="arusha-park-overview__text">
              <p className="arusha-park-section__eyebrow">About the park</p>
              <h2 id="overview-heading">Gateway to the Northern Circuit</h2>
              <p>
                Arusha National Park lies just northeast of Arusha town, wrapping
                around the lower slopes of Mount Meru Tanzania&apos;s second-highest
                peak. Few parks offer such variety in so compact an area: montane
                rainforest, open grassland, alkaline lakes, and the swamp-filled
                Ngurdoto Crater all within a single day&apos;s drive.
              </p>
              <p>
                Unlike the larger savanna parks, Arusha has no lions in the main
                visitor areas, making guided forest walks and canoe safaris on
                Momella Lakes safe and rewarding. It is the perfect opener before
                a Meru climb or a longer Serengeti and Ngorongoro itinerary.
              </p>
              <ul className="arusha-park-overview__details">
                <Reveal as="li" className="arusha-park-overview__detail" delay={0}>
                  <span className="arusha-park-overview__detail-label">Location</span>
                  <span className="arusha-park-overview__detail-value">Near Arusha town</span>
                </Reveal>
                <Reveal as="li" className="arusha-park-overview__detail" delay={80}>
                  <span className="arusha-park-overview__detail-label">Park size</span>
                  <span className="arusha-park-overview__detail-value">552 km²</span>
                </Reveal>
                <Reveal as="li" className="arusha-park-overview__detail" delay={160}>
                  <span className="arusha-park-overview__detail-label">Established</span>
                  <span className="arusha-park-overview__detail-value">1960</span>
                </Reveal>
                <Reveal as="li" className="arusha-park-overview__detail" delay={240}>
                  <span className="arusha-park-overview__detail-label">Best for</span>
                  <span className="arusha-park-overview__detail-value">Giraffes &amp; Meru</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="arusha-park-overview__image" variant="right" delay={200}>
              <SafeImage
                className="arusha-park-overview__img"
                src={ARUSHA_IMAGES.overview}
                alt="Mount Meru foothills and forest in Arusha National Park"
              />
              <span className="arusha-park-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── Safari packages ──── */}
      <section
        id="arusha-park-packages"
        className="arusha-park-section arusha-park-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="arusha-park-section__header">
            <p className="arusha-park-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Arusha National Park packages</h2>
            <p className="arusha-park-section__intro">
              From a day trip to a Meru trek opener each package is tailored to
              your dates, pace, and how you want to begin the Northern Circuit.
            </p>
          </Reveal>
          <ul className="arusha-park-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="arusha-park-packages__card"
                delay={index * 100}
              >
                <div className="arusha-park-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="arusha-park-packages__duration">{pkg.duration}</span>
                </div>
                <div className="arusha-park-packages__card-body">
                  <span className="arusha-park-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="arusha-park-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="arusha-park-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="arusha-park-wildlife"
        className="arusha-park-section arusha-park-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="arusha-park-section__header">
            <p className="arusha-park-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">Life beneath Mount Meru</h2>
            <p className="arusha-park-section__intro">
              From treetop colobus monkeys to lakeside flamingos Arusha National
              Park packs diverse habitats and species into a single unforgettable day.
            </p>
          </Reveal>
          <ul className="arusha-park-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="arusha-park-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="arusha-park-wildlife__icon-frame">
                  <span className="arusha-park-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="arusha-park-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="arusha-park-gallery"
        className="arusha-park-section arusha-park-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="arusha-park-section__header">
            <p className="arusha-park-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Arusha National Park</h2>
            <p className="arusha-park-section__intro">
              From Meru&apos;s volcanic summit to Momella&apos;s flamingo shores
              the many landscapes of Tanzania&apos;s most accessible park.
            </p>
          </Reveal>
          <div className="arusha-park-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="arusha-park-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="arusha-park-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="arusha-park-seasons"
        className="arusha-park-section arusha-park-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="arusha-park-section__header">
            <p className="arusha-park-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Arusha National Park</h2>
            <p className="arusha-park-section__intro">
              Arusha is a rewarding year-round destination dry months suit Meru
              treks and lake drives, while green season brings lush forest and birds.
            </p>
          </Reveal>
          <ul className="arusha-park-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="arusha-park-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`arusha-park-seasons__card-badge arusha-park-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="arusha-park-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="arusha-park-experiences"
        className="arusha-park-section arusha-park-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="arusha-park-section__header">
            <p className="arusha-park-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore Arusha National Park</h2>
            <p className="arusha-park-section__intro">
              Game drives, forest walks, canoe safaris, and Meru trekking
              Arusha offers more activity variety per square kilometre than almost
              any park in Tanzania.
            </p>
          </Reveal>
          <ul className="arusha-park-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="arusha-park-experiences__card"
                delay={index * 120}
              >
                <div className="arusha-park-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="arusha-park-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="arusha-park-getting-there"
        className="arusha-park-section arusha-park-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="arusha-park-getting-there__inner">
            <Reveal className="arusha-park-getting-there__copy">
              <p className="arusha-park-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Arusha National Park</h2>
              <p>
                The park entrance at Ngongongare gate lies roughly 25 km from
                Arusha town and about 60 km from Kilimanjaro International
                Airport making it the easiest national park to reach on the
                Northern Circuit.
              </p>
              <ul className="arusha-park-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="arusha-park-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Arusha National Park location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127168!2d36.85!3d-3.25!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1837134567890abc%3A0xabcdef1234567890!2sArusha%20National%20Park!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="arusha-park-cta" aria-labelledby="arusha-park-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="arusha-park-cta__inner">
              <h2 id="arusha-park-cta-heading">
                Ready to explore Arusha National Park?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made day
                safari, Meru trek, or Northern Circuit opener starting at
                Tanzania&apos;s most accessible park.
              </p>
              <a
                className="arusha-park-btn arusha-park-btn--primary arusha-park-btn--lg"
                href="#contact"
              >
                Get a free Arusha quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default ArushaPark;
